import type { BodyBlock, Hotspot, Tour } from './types';

export class TourValidationError extends Error {}

function isVec3(v: unknown): v is [number, number, number] {
  return (
    Array.isArray(v) &&
    v.length === 3 &&
    v.every((x) => typeof x === 'number' && Number.isFinite(x))
  );
}

function parseBodyBlock(b: unknown, where: string): BodyBlock {
  if (!b || typeof b !== 'object') {
    throw new TourValidationError(`${where}: body block must be an object`);
  }
  const obj = b as Record<string, unknown>;
  switch (obj.type) {
    case 'p':
      if (typeof obj.text !== 'string') {
        throw new TourValidationError(`${where}: p.text must be string`);
      }
      return { type: 'p', text: obj.text };
    case 'h':
      if (typeof obj.text !== 'string') {
        throw new TourValidationError(`${where}: h.text must be string`);
      }
      return { type: 'h', text: obj.text };
    case 'image':
      if (typeof obj.src !== 'string' || typeof obj.alt !== 'string') {
        throw new TourValidationError(`${where}: image.src and image.alt required`);
      }
      return {
        type: 'image',
        src: obj.src,
        alt: obj.alt,
        caption: typeof obj.caption === 'string' ? obj.caption : undefined
      };
    case 'pano360':
      if (
        typeof obj.src !== 'string' ||
        typeof obj.thumbnail !== 'string' ||
        typeof obj.alt !== 'string'
      ) {
        throw new TourValidationError(`${where}: pano360 requires src, thumbnail, alt`);
      }
      return {
        type: 'pano360',
        src: obj.src,
        thumbnail: obj.thumbnail,
        alt: obj.alt,
        initialYaw: typeof obj.initialYaw === 'number' ? obj.initialYaw : undefined,
        initialPitch: typeof obj.initialPitch === 'number' ? obj.initialPitch : undefined
      };
    default:
      throw new TourValidationError(`${where}: unknown body block type "${String(obj.type)}"`);
  }
}

function parseHotspot(h: unknown, idx: number): Hotspot {
  const where = `hotspots[${idx}]`;
  if (!h || typeof h !== 'object') throw new TourValidationError(`${where} must be object`);
  const obj = h as Record<string, unknown>;
  if (typeof obj.id !== 'string') throw new TourValidationError(`${where}.id must be string`);
  if (typeof obj.title !== 'string') throw new TourValidationError(`${where}.title must be string`);
  if (!isVec3(obj.anchor)) throw new TourValidationError(`${where}.anchor must be [x,y,z]`);
  const cam = obj.camera as Record<string, unknown> | undefined;
  if (!cam || !isVec3(cam.position) || !isVec3(cam.target)) {
    throw new TourValidationError(`${where}.camera.position and .target must be [x,y,z]`);
  }
  const orbit = obj.orbit as Record<string, unknown> | undefined;
  const body = Array.isArray(obj.body)
    ? obj.body.map((b, i) => parseBodyBlock(b, `${where}.body[${i}]`))
    : [];
  return {
    id: obj.id,
    title: obj.title,
    kicker: typeof obj.kicker === 'string' ? obj.kicker : undefined,
    anchor: obj.anchor,
    camera: {
      position: cam.position,
      target: cam.target,
      fov: typeof cam.fov === 'number' ? cam.fov : undefined
    },
    orbit: orbit
      ? {
          minPolar: typeof orbit.minPolar === 'number' ? orbit.minPolar : undefined,
          maxPolar: typeof orbit.maxPolar === 'number' ? orbit.maxPolar : undefined,
          minDistance: typeof orbit.minDistance === 'number' ? orbit.minDistance : undefined,
          maxDistance: typeof orbit.maxDistance === 'number' ? orbit.maxDistance : undefined,
          minAzimuth: typeof orbit.minAzimuth === 'number' ? orbit.minAzimuth : undefined,
          maxAzimuth: typeof orbit.maxAzimuth === 'number' ? orbit.maxAzimuth : undefined
        }
      : undefined,
    body
  };
}

export function parseTour(raw: unknown): Tour {
  if (!raw || typeof raw !== 'object') {
    throw new TourValidationError('tour must be an object');
  }
  const obj = raw as Record<string, unknown>;
  if (obj.version !== 1) {
    throw new TourValidationError('tour.version must be 1');
  }
  const scene = obj.scene as Record<string, unknown> | undefined;
  if (!scene || typeof scene.asset !== 'string') {
    throw new TourValidationError('tour.scene.asset must be a string');
  }
  const defaults = obj.defaults as Record<string, unknown> | undefined;
  if (
    !defaults ||
    typeof defaults.cameraFlyDurationMs !== 'number' ||
    (defaults.easing !== 'easeInOutCubic' && defaults.easing !== 'easeInOutQuad')
  ) {
    throw new TourValidationError('tour.defaults invalid');
  }
  if (!Array.isArray(obj.hotspots) || obj.hotspots.length === 0) {
    throw new TourValidationError('tour.hotspots must be a non-empty array');
  }
  return {
    version: 1,
    scene: { asset: scene.asset },
    defaults: {
      cameraFlyDurationMs: defaults.cameraFlyDurationMs,
      easing: defaults.easing
    },
    hotspots: obj.hotspots.map(parseHotspot)
  };
}

export async function loadTour(url = '/tour.json'): Promise<Tour> {
  const res = await fetch(url);
  if (!res.ok) throw new TourValidationError(`failed to fetch ${url}: ${res.status}`);
  const raw = await res.json();
  return parseTour(raw);
}
